import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { MaterialModule } from '../../shared/material/material.module';
import { Router } from '@angular/router';
import {
  ProfileProviderPF,
  ProfileProviderPJ,
  UserRef,
} from '../../core/generated';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { AuthCustomService } from '../../security/auth-custom.service';
import { NotificationCountService } from '../../shared/services/notification-count.service';
import { ProfileProviderPFStateService } from '../../shared/services/profile-provider-pf-state.service';
import { ProfileProviderPJStateService } from '../../shared/services/profile-provider-pj-state.service';
import { Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [MaterialModule],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  // Services
  private authCustomService = inject(AuthCustomService);
  private UserPreferencesCacheService = inject(UserPreferencesCacheService);
  private router = inject(Router);
  public notificationCountService = inject(NotificationCountService);
  private profileProviderPFStateService = inject(ProfileProviderPFStateService);
  private profileProviderPJStateService = inject(ProfileProviderPJStateService);
  @Output() toggleSideBarForMe = new EventEmitter<void>();
  @Output() toggleSideBarNotificationForMe = new EventEmitter<void>();

  // Inputs
  @Input() mobileQuery: any;
  @Input() sidebarOpened: boolean = false;

  // Component state
  user!: UserRef;
  profileProviderPF: ProfileProviderPF | null = null;
  profileProviderPJ: ProfileProviderPJ[] = [];
  notificationCount = 0;
  isLoadingProfiles = true;

  // For subscription management
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Subscribe to notification count
    this.notificationCountService.notificationCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.notificationCount = count;
      });

    // Load user and profiles
    this.loadUserAndProfiles();
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserAndProfiles(): void {
    this.UserPreferencesCacheService
      .getUser()
      .pipe(take(1))
      .subscribe((user) => {
        if (!user) return;

        this.user = user;

        // Force user to complete profile if CPF is missing
        if (!user.cpf || user.cpf === '') {
          this.router.navigate(['/my-account/user']);
          return;
        }

        // Load profiles if email exists
        if (user.email) {
          this.loadProfiles(user.email);
        }
      });
  }

  private loadProfiles(email: string): void {
    this.isLoadingProfiles = true;
    let pfLoaded = false;
    let pjLoaded = false;

    const checkLoadingComplete = () => {
      if (pfLoaded && pjLoaded) {
        this.isLoadingProfiles = false;
      }
    };

    // Load provider PF profile
    this.profileProviderPFStateService.loadByEmail(email);
    this.profileProviderPFStateService.profileProviderPF$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          // Atualiza o perfil apenas se existir
          if (profile) {
            this.profileProviderPF = profile;
          }
          // Marca como carregado independente do resultado (pode ser null)
          if (!pfLoaded) {
            pfLoaded = true;
            checkLoadingComplete();
          }
        },
        error: (err) => {
          console.error('Erro ao carregar perfil PF:', err);
          // Marca como carregado mesmo em caso de erro
          if (!pfLoaded) {
            pfLoaded = true;
            checkLoadingComplete();
          }
        }
      });

    // Load provider PJ profiles
    this.profileProviderPJStateService.loadByEmail(email);
    this.profileProviderPJStateService.profileProviderPJ$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profiles) => {
          // Atualiza os perfis apenas se existirem
          if (profiles) {
            this.profileProviderPJ = profiles;
          }
          // Marca como carregado independente do resultado (pode ser null ou array vazio)
          if (!pjLoaded) {
            pjLoaded = true;
            checkLoadingComplete();
          }
        },
        error: (err) => {
          console.error('Erro ao carregar perfis PJ:', err);
          // Marca como carregado mesmo em caso de erro
          if (!pjLoaded) {
            pjLoaded = true;
            checkLoadingComplete();
          }
        }
      });
  }

  // Expose properties and methods for the template
  get userEmail(): string | undefined {
    return this.authCustomService.userEmail;
  }

  isAuthenticated(): boolean {
    return this.authCustomService.hasValidToken();
  }

  hasAdminPermission(): boolean {
    return this.authCustomService.hasPermission('ADMIN');
  }

  login(): void {
    this.authCustomService.login();
  }

  logout(): void {
    this.authCustomService.logout();
  }

  get theme(): string {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }

  set theme(name: string) {
    localStorage.setItem('theme', name);
    if (name === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.setAttribute('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.removeAttribute('theme');
    }
  }

  setScreenMode(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
  }

  toggleTheme(): void {
    this.setScreenMode();
  }

  toggleSideBar(): void {
    this.toggleSideBarForMe.emit();
  }

  toggleSideBarNotification(): void {
    this.toggleSideBarNotificationForMe.emit();
  }

  hasNotification(): number {
    return this.notificationCount || 10;
  }
}
